import React from "react";

import { makeStyles, createStyles, Theme, IconButton } from "@material-ui/core";
import { AddCircleOutline } from "@material-ui/icons";
import clsx from "clsx";
import { BasewordChip } from "./BasewordChip";
import { SynonymChip } from "./SynonymChip";
import { useMessageBar, ColorInfo } from "../../helpers";
import { SynonymAddTextField } from "./SynonymAddTextField";
import { EditableSynonymGroup, EditableSynonymSubgroup } from "../../types";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "inline-flex",
      marginRight: theme.spacing(0.5),
    },
    group: {
      display: "inline-flex",
    },
    synonyms: {
      display: "inline-flex",
    },
    form: {
      display: "inline-flex",
    },
    iconButton: {
      marginTop: theme.spacing(0.5),
      marginLeft: theme.spacing(0.5),
      width: "24px",
      height: "24px",
      padding: 0,
      display: "inline",
    },
  })
);

/** テキストフィールド入力チェック */
const validate = (
  group: EditableSynonymGroup,
  text: string
): [boolean, string] => {
  let valid = true;
  let message = "";

  // 空チェック
  if (text === "") {
    valid = false;
    message = "同義語を入力してください";
  }

  // 重複チェック
  if (
    text === group.baseword ||
    (group.editableSubgroups &&
      group.editableSubgroups.length > 0 &&
      group.editableSubgroups
        .map(editableSubgroup => editableSubgroup.word)
        .includes(text))
  ) {
    valid = false;
    message = `同義語「${text}」は既に登録されています`;
  }
  return [valid, message];
};

interface Props {
  group: EditableSynonymGroup;
  color: ColorInfo;
  disabled: boolean;
  onExpandSynonyms: (target: EditableSynonymGroup) => void;
  onShrinkSynonyms: (target: EditableSynonymGroup) => void;
  onChangeDeletingSynonym: (
    target: EditableSynonymSubgroup,
    deleting: boolean
  ) => void;
  onAddingSynonym: (target: EditableSynonymGroup) => void;
  onChangeEditText: (target: EditableSynonymGroup, newText: string) => void;
  onEditing: (target: EditableSynonymGroup) => void;
  onCancelEdit: (target: EditableSynonymGroup) => void;
}

/** 同義語チップグループ */
export function SynonymChipGroup(props: Props) {
  const classes = useStyles();
  const enqueueMessge = useMessageBar();

  const {
    group,
    color,
    disabled,
    onExpandSynonyms,
    onShrinkSynonyms,
    onChangeDeletingSynonym,
    onAddingSynonym,
    onChangeEditText,
    onEditing,
    onCancelEdit,
  } = props;

  // 追加テキストフィールドの参照
  const endOfInputRef = React.createRef<HTMLSpanElement>();
  const inputRef = React.createRef<HTMLInputElement>();
  // 追加テキストフィールドを見える位置までスクロールする
  React.useEffect(() => {
    if (group.editing) {
      // テキストフィールドにフォーカスする
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
      // テキストフィールドが表示されるようにスクロールする
      if (endOfInputRef && endOfInputRef.current) {
        endOfInputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }
  }, [group.editing]);

  // 同義語追加
  const handleAdd = () => {
    // 入力チェック
    const [valid, message] = validate(group, group.editText.trim());
    if (!valid) {
      enqueueMessge(message, "warning");
      return;
    }
    // ハンドラー呼び出し
    onAddingSynonym(group);
  };

  // 動的に設定する語彙チップのスタイル
  const dynamicSytles = makeStyles((theme: Theme) =>
    createStyles({
      // 基本語彙チップ
      basewordChip: {
        backgroundColor: color.rgbaThick,
        "&:hover": {
          backgroundColor: color.rgbaThick,
        },
        "&:focus": {
          backgroundColor: color.rgbaThick,
        },
      },
      // 同義語彙チップ（通常）
      synonymChip: {
        backgroundColor: color.rgbaThin,
        "&:hover": {
          backgroundColor: color.rgbaThin,
        },
        "&:focus": {
          backgroundColor: color.rgbaThin,
        },
      },
      // 同義語彙チップ（削除）
      synonymChipOff: {
        backgroundColor: theme.palette.grey[300],
        "&:hover": {
          backgroundColor: theme.palette.grey[300],
        },
        "&:focus": {
          backgroundColor: theme.palette.grey[300],
        },
      },
    })
  );
  const dynamicClasses = dynamicSytles();

  // 同義語サブグループ情報の構築
  let subgroupCount = 0;
  let synonymElems: React.ReactNode = <div />;
  if (group.editableSubgroups && group.editableSubgroups.length > 0) {
    // 同義語サブグループ数
    subgroupCount = group.editableSubgroups.length;
    // 同義語チップ要素リスト
    synonymElems = group.editableSubgroups.map(
      (editableSubgroup: EditableSynonymSubgroup) => (
        <SynonymChip
          key={editableSubgroup.word}
          text={editableSubgroup.word}
          className={
            editableSubgroup.deleting
              ? clsx(dynamicClasses.synonymChipOff) // チップが削除対象の場合
              : clsx(dynamicClasses.synonymChip)
          }
          onDelete={() => {
            onChangeDeletingSynonym(
              editableSubgroup,
              !editableSubgroup.deleting // フラグ反転
            );
          }}
          deleting={editableSubgroup.deleting}
          adding={editableSubgroup.adding}
          disabled={disabled}
        />
      )
    );
  }

  return (
    <div className={classes.root}>
      {/*
        基本語彙チップ

       */}
      <BasewordChip
        className={clsx(dynamicClasses.basewordChip)}
        text={group.baseword}
        expand={group.expand}
        expandable
        count={subgroupCount}
        showCount
        showZero={false}
        onExpand={() => {
          onExpandSynonyms(group);
        }}
        onShrink={() => {
          onShrinkSynonyms(group);
        }}
      />
      {/*
        同義語彙リスト

      */}
      {group.expand && (
        <div className={classes.group}>
          <div className={classes.synonyms}>{synonymElems}</div>
          <div className={classes.form}>
            {/* 追加テキストフィールド */}
            {group.editing && (
              <div style={{ display: "inline-flex" }}>
                <SynonymAddTextField
                  text={group.editText}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onChangeEditText(group, event.target.value);
                  }}
                  onAdd={handleAdd}
                  onCancel={() => {
                    onCancelEdit(group);
                  }}
                  inputRef={inputRef}
                />
                {/* ScrollIntoView位置合わせ用のダミーspan要素 */}
                <span ref={endOfInputRef} />
              </div>
            )}
            {/* 追加ボタン */}
            {!group.editing && (
              <IconButton
                onClick={() => {
                  onEditing(group);
                }}
                className={classes.iconButton}
                disabled={disabled}
              >
                <AddCircleOutline />
              </IconButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
